import React from 'react';
import {
  Users, Plus, Search, Edit, Trash2, AlertTriangle, UtensilsCrossed, ShieldAlert
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import LoadingSpinner from '../LoadingSpinner';
import { formatMontant, formatNumber, formatJoursSemaine } from './cantineUtils';

const STATUT_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'actif', label: 'Actif' },
  { value: 'suspendu', label: 'Suspendu' },
  { value: 'resilie', label: 'Résilié' }
];

const REGIME_OPTIONS = [
  { value: 'all', label: 'Tous les régimes' },
  { value: 'forfait', label: 'Forfait' },
  { value: 'occasionnel', label: 'Occasionnel' }
];

const StatutBadge = ({ statut }) => {
  if (statut === 'actif') {
    return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Actif</span>;
  }
  if (statut === 'suspendu') {
    return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Suspendu</span>;
  }
  return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Résilié</span>;
};

const RegimeBadge = ({ regime }) => (
  regime === 'forfait'
    ? <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Forfait</span>
    : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Occasionnel</span>
);

const CantineInscriptionsSection = ({
  isLoading,
  resume,
  inscriptions,
  sections,
  classes,
  filterStatut, setFilterStatut,
  filterRegime, setFilterRegime,
  filterSection, onSectionChange,
  filterClasse, setFilterClasse,
  search, setSearch,
  onNouvelleInscription,
  onModifierInscription,
  onSupprimerInscription,
  onGererRestrictions
}) => {
  const classesFiltrees = filterSection
    ? classes.filter((c) => String(c.section_id) === String(filterSection))
    : classes;

  return (
    <div className="space-y-4">
      {/* Cartes de synthèse -- patterns MaCaisse */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-2 border-blue-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Users size={22} className="text-blue-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Inscrits à la cantine</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Actifs</span>
                <span className="font-semibold text-green-600">{formatNumber(resume.actifs)}</span>
              </div>
              <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Total</span>
                <span className="text-xl font-bold text-blue-700">{formatNumber(resume.total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-2 border-green-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <UtensilsCrossed size={22} className="text-green-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Abonnés au forfait</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-green-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Part des inscrits</span>
                <span className="font-semibold text-gray-700">
                  {resume.total > 0 ? Math.round((resume.forfait / resume.total) * 100) : 0}%
                </span>
              </div>
              <div className="border-t border-green-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Forfait</span>
                <span className="text-xl font-bold text-green-600">{formatNumber(resume.forfait)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-2 border-purple-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Users size={22} className="text-purple-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Occasionnels</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-purple-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">Paient à la journée</span>
                <span className="font-semibold text-gray-700">
                  {resume.total > 0 ? Math.round((resume.occasionnel / resume.total) * 100) : 0}%
                </span>
              </div>
              <div className="border-t border-purple-200 pt-2 mt-2 flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Occasionnel</span>
                <span className="text-xl font-bold text-purple-700">{formatNumber(resume.occasionnel)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-2 border-red-200">
          <CardContent className="p-5">
            <div className="w-11 h-11 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <ShieldAlert size={22} className="text-red-600" />
            </div>
            <p className="text-gray-600 text-sm mb-3 font-semibold">Allergies critiques</p>
            <div className="space-y-2 bg-white rounded-lg p-3 border border-red-100">
              <div className="flex justify-between items-center gap-3 text-sm">
                <span className="text-gray-600">À signaler en cuisine</span>
                <span className="font-semibold text-red-600">Priorité</span>
              </div>
              <div className="border-t border-red-200 pt-2 mt-2">
                <div className="flex justify-between items-center gap-3">
                  <span className="text-gray-700 font-semibold">Élèves concernés</span>
                  <span className="text-xl font-bold text-red-600">{formatNumber(resume.avec_restriction_critique)}</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Une allergie critique doit être connue de l'équipe.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des inscriptions */}
      <Card>
        <CardHeader>
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <CardTitle className="text-xl text-[#246990] flex items-center gap-2">
                <Users size={24} />
                Inscriptions à la cantine
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                élèves inscrits, régime de facturation et restrictions alimentaires
              </p>
            </div>
            <Button
              size="default"
              className="bg-[#246990] hover:bg-[#1f5a7d] text-white"
              onClick={onNouvelleInscription}
            >
              <Plus size={16} className="mr-2" />
              Inscrire un élève
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filtres */}
          <div className="border-2 border-gray-200 rounded-lg bg-gray-50/50 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Rechercher un élève..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-white"
                />
              </div>

              <Select value={filterSection || 'all'} onValueChange={(v) => onSectionChange(v === 'all' ? '' : v)}>
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue placeholder="Toutes les sections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les sections</SelectItem>
                  {sections.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterClasse || 'all'} onValueChange={(v) => setFilterClasse(v === 'all' ? '' : v)}>
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue placeholder="Toutes les classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les classes</SelectItem>
                  {classesFiltrees.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="grid grid-cols-2 gap-3">
                <Select value={filterRegime} onValueChange={setFilterRegime}>
                  <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REGIME_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Select value={filterStatut} onValueChange={setFilterStatut}>
                  <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Tableau */}
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <LoadingSpinner message="Chargement des inscriptions..." color="#246990" />
            </div>
          ) : inscriptions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
              <UtensilsCrossed size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 font-medium">Aucune inscription trouvée</p>
              <p className="text-gray-500 text-sm mt-2">
                Inscrivez un élève à la cantine pour le voir apparaître ici
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-100 border-b border-blue-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Élève</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Classe</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Régime</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Jours</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-900 uppercase">Tarif cantine</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Restrictions</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Statut</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {inscriptions.map((inscription) => (
                    <tr key={inscription.id} className="hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {inscription.prenom} {inscription.nom}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {inscription.matricule || 'Sans matricule'}
                        </p>
                      </td>

                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{inscription.classe_nom || '—'}</p>
                        <p className="text-xs text-gray-500">{inscription.section_nom || ''}</p>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <RegimeBadge regime={inscription.regime_facturation} />
                      </td>

                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">
                          {inscription.regime_facturation === 'forfait'
                            ? formatJoursSemaine(inscription.jours_semaine)
                            : <span className="text-gray-400">À la demande</span>}
                        </p>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <p className="text-sm font-semibold text-[#246990]">
                          {formatMontant(inscription.frais_cantine)}
                        </p>
                      </td>

                      <td className="px-4 py-3 text-center">
                        {inscription.nb_restrictions_critiques > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertTriangle size={12} className="mr-1" />
                            {inscription.nb_restrictions_critiques} critique(s)
                          </span>
                        ) : inscription.nb_restrictions > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {inscription.nb_restrictions}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <StatutBadge statut={inscription.statut} />
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Allergies et régimes alimentaires"
                            onClick={() => onGererRestrictions(inscription)}
                          >
                            <ShieldAlert size={16} />
                          </button>
                          <button
                            className="p-2 text-[#246990] hover:bg-[#246990]/10 rounded-lg transition-colors"
                            title="Modifier l'inscription"
                            onClick={() => onModifierInscription(inscription)}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Retirer de la cantine"
                            onClick={() => onSupprimerInscription(inscription)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CantineInscriptionsSection;
